import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Loader2, MapPin, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin home chip — live % of talent profiles with geocodes.
 * Provides a one-click "Re-run backfill" so the roster stays self-healing.
 */
const GeocodeCoverageChip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/geocoding/admin/coverage');
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const runBackfill = async () => {
    setRunning(true);
    try {
      const res = await api.post('/geocoding/admin/backfill');
      if (res.data.status === 'already_running') {
        toast.info('Backfill already in progress — coverage will update shortly');
      } else {
        toast.success('Backfill started — this may take a few minutes');
      }
      // Poll coverage a few times so the user sees progress
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await fetch();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Geocode coverage…
      </div>
    );
  }
  if (!data) return null;

  const pct = data.coverage_pct;
  const missing = data.missing || 0;
  const color = pct >= 90
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : pct >= 70
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border ${color} px-3 py-1.5 text-xs`}
      data-testid="geocode-coverage-chip"
    >
      <MapPin className="w-3 h-3" />
      <span className="font-medium">{pct}% geocoded</span>
      <span className="text-gray-500">· {data.geocoded}/{data.total}</span>
      {missing > 0 && (
        <Button
          size="sm"
          variant="outline"
          onClick={runBackfill}
          disabled={running}
          className="h-6 px-2 text-[11px] border-current"
          data-testid="geocode-backfill-btn"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          <span className="ml-1">Re-run backfill</span>
        </Button>
      )}
    </div>
  );
};

export default GeocodeCoverageChip;
